// Landlocked wall styling (sea Phase 0 follow-up): the two wall rows must read
// as one tall, dark, snow-free barrier rather than two more snowy peaks.
//
//  - height: `wallBoost(row)` is added on top of the pinned level height (so
//    the ridge stands above any interior massif); it feeds terrainLevels'
//    smoothed heights, i.e. EVERY consumer of tileSurfaceY (prism plateau,
//    outlines, scatter, units) sees the same surface;
//  - look: wall tiles carry shader code WALL_TILE_CODE in the tile program's
//    existing aTone.z high bits (volcanoTileShader.ts) => no snow, dark rock.
//
// Pure; wall tiles are the ones `applyLandlockedWall` marks (`wall: true`,
// level 12 - row).

import type { SceneNode } from '@aod/protocol';

/** Extra world height per wall row (0 = outermost). Beyond the table: none. */
export const WALL_BOOST: readonly number[] = [0.35, 0.2];
/** aTone.z code (x64) for a wall tile; volcano codes are 0..3, so this is bit 8. */
export const WALL_TILE_CODE = 4;
const TOP_LEVEL = 12;

/** Extra height of a wall row; 0 for rows past the table or invalid input. */
export function wallBoost(row: number): number {
  return WALL_BOOST[Math.max(0, Math.floor(row))] ?? 0;
}

export interface WallStyle {
  /** "q,r" -> extra height. */
  readonly boosts: Map<string, number>;
  /** "q,r" -> WALL_TILE_CODE. */
  readonly codes: Map<string, number>;
}

/** Wall tiles' styling maps; both empty when no tile carries `wall: true`. */
export function wallStyle(tiles: ReadonlyArray<SceneNode>): WallStyle {
  const boosts = new Map<string, number>();
  const codes = new Map<string, number>();
  for (const t of tiles) {
    const p = t.props as { q?: number; r?: number; wall?: boolean; level?: number } | undefined;
    if (!p?.wall || p.q === undefined || p.r === undefined) continue;
    const k = `${p.q},${p.r}`;
    boosts.set(k, wallBoost(TOP_LEVEL - (p.level ?? TOP_LEVEL)));
    codes.set(k, WALL_TILE_CODE);
  }
  return { boosts, codes };
}
